import List from '@jetbrains/ring-ui/components/list/list'
import {memo, useCallback} from 'react'

import {usePermission} from '../../../../../../hooks/usePermission'
import {DialogType, InvestigationStates, Permission, stringifyId} from '../../../../../../types'
import ArrowPopupMenu from '../../../../../common/ArrowPopupMenu/ArrowPopupMenu'
import RouterLink from '../../../../../common/RouterLink/RouterLink'
import {getFullBuildLogLinkParams} from '../../../../BuildLog/BuildLog.utils'
import {ErrorTypes} from '../../../BuildProblems.constants'

import type {Props} from './BuildProblemActions.types'

import styles from './BuildProblemActions.css'

function BuildProblemActions({
  className,
  icon,
  investigationInCurrentScope,
  problemProjectId,
  problemOccurrence,
  assignInvestigationHandler,
  openDialog,
}: Props) {
  const hasPermissionForInvestigation = usePermission(
    Permission.ASSIGN_INVESTIGATION,
    problemProjectId,
  )
  const hasPermissionForManageProblems = usePermission(
    Permission.MANAGE_BUILD_PROBLEMS,
    problemProjectId,
  )
  const {id, build, currentlyInvestigated, currentlyMuted, logAnchor, type} = problemOccurrence
  const onAssignInvestigation = useCallback(() => {
    assignInvestigationHandler()
  }, [assignInvestigationHandler])
  const onFixInvestigation = useCallback(() => {
    assignInvestigationHandler(true)
  }, [assignInvestigationHandler])
  const onShowInvestigationHistory = useCallback(() => {
    openDialog(stringifyId(id), DialogType.INVESTIGATION_HISTORY)
  }, [id, openDialog])
  const elements = []
  elements.push({
    rgItemType: List.ListProps.Type.ITEM,
    title: '',
    label: 'Show investigation history',
    onClick: onShowInvestigationHistory,
  })

  if (hasPermissionForInvestigation) {
    elements.push({
      rgItemType: List.ListProps.Type.ITEM,
      title: '',
      label:
        currentlyInvestigated === true ||
        investigationInCurrentScope?.state === InvestigationStates.FIXED
          ? 'Reassign investigation...'
          : 'Assign investigation...',
      onClick: onAssignInvestigation,
    })

    if (
      currentlyInvestigated === true &&
      investigationInCurrentScope?.state !== InvestigationStates.FIXED
    ) {
      elements.push({
        rgItemType: List.ListProps.Type.ITEM,
        title: '',
        label: 'Fix investigation...',
        onClick: onFixInvestigation,
      })
    }
  }

  if (hasPermissionForManageProblems) {
    elements.push({
      rgItemType: List.ListProps.Type.ITEM,
      title: '',
      label: currentlyMuted === true ? 'Unmute problem...' : 'Mute problem...',
      onClick: onAssignInvestigation,
    })
  }

  if (build?.id != null && Number(logAnchor) != null && type !== ErrorTypes.TC_USER_PROVIDED) {
    elements.push({
      rgItemType: List.ListProps.Type.CUSTOM,
      className: styles.customLinkWrapper,
      template: (
        <RouterLink
          params={getFullBuildLogLinkParams({
            buildId: build.id,
            line: logAnchor,
            expandState: [Number(logAnchor)],
            logFilter: 'debug',
          })}
          className={styles.customLink}
        >
          {'Show in Build Log'}
        </RouterLink>
      ),
    })
  }

  return <ArrowPopupMenu className={className} elements={elements} icon={icon} />
}

export default memo<Props>(BuildProblemActions)
